import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";
import SignupsBySourcePanel from "./SignupsBySourcePanel";

/**
 * Gate 6 · Slice 6.3 preview.
 *
 * This is the SCAFFOLD for the Insights dashboard. It reads the
 * Slice 6.2 endpoints that already ship (attributions,
 * attributions/freshness, attributions/worker/status) and lays out
 * the four cards the full dashboard will render:
 *
 *   1. Attribution freshness — per-source last pulled + receipts covered
 *   2. Latest attribution rows — most recent 5 with metric snapshot
 *   3. Worker health — heartbeat + last tick summary
 *   4. Coverage — receipts with any attribution vs total FB receipts
 *
 * When Slice 6.3 lands proper (rollups + charts) this page will grow
 * a top time-range picker and per-campaign breakdowns. The read
 * endpoints and the layout are already contract-tested.
 */

function timeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.floor((now - then) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function freshnessTone(lastPulledAt) {
  if (!lastPulledAt) return "muted";
  const ageMs = Date.now() - new Date(lastPulledAt).getTime();
  const hours = ageMs / 3_600_000;
  if (hours <= 8) return "success";
  if (hours <= 24) return "warn";
  return "danger";
}

function metricRow(label, value, testId) {
  return (
    <div className="bo-metric" data-testid={testId}>
      <div className="bo-metric-label">{label}</div>
      <div className="bo-metric-value">{value ?? "—"}</div>
    </div>
  );
}

export default function InsightsOverview() {
  const [freshness, setFreshness] = useState(null);
  const [attributions, setAttributions] = useState(null);
  const [worker, setWorker] = useState(null);
  const [campaignRollup, setCampaignRollup] = useState(null);
  const [variantRollup, setVariantRollup] = useState(null);
  const [learningCandidates, setLearningCandidates] = useState(null);
  const [err, setErr] = useState(null);
  const [ticking, setTicking] = useState(false);
  const [tickResult, setTickResult] = useState(null);
  const [promoting, setPromoting] = useState({}); // { [variantId]: true }
  const [promoteResult, setPromoteResult] = useState({}); // { [variantId]: {proposal_id,status} }

  async function loadAll() {
    try {
      setErr(null);
      const [f, a, w, rc, rv, lq] = await Promise.all([
        api.get("/attributions/freshness"),
        api.get("/attributions?limit=5"),
        api.get("/attributions/worker/status").catch(() => ({ data: null })),
        api.get("/attributions/rollups/campaigns").catch(() => ({ data: null })),
        api.get("/attributions/rollups/variants").catch(() => ({ data: null })),
        api.get("/learning-queue/candidates").catch(() => ({ data: null })),
      ]);
      setFreshness(f.data || null);
      setAttributions(a.data?.attributions || []);
      setWorker(w?.data || null);
      setCampaignRollup(rc?.data || null);
      setVariantRollup(rv?.data || null);
      setLearningCandidates(lq?.data || null);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      await loadAll();
      if (!alive) return;
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleTickNow() {
    setTicking(true);
    setTickResult(null);
    try {
      const r = await api.post("/attributions/worker/tick-now", { test_mode: false });
      setTickResult(r.data);
      await loadAll();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setTicking(false);
    }
  }

  // EBL-1: Promote a winning variant into a pending IntelligenceProposal.
  // The owner still has to accept via /intelligence/proposals/{id}/accept
  // before the learning enters governed truth — performance is EVIDENCE,
  // not authority.
  async function handlePromoteLearning({ variantAxis, variantId }) {
    if (!variantAxis || !variantId) return;
    setPromoting((p) => ({ ...p, [variantId]: true }));
    try {
      const r = await api.post("/learning-queue/promote", {
        variant_axis: variantAxis,
        variant_id: variantId,
      });
      setPromoteResult((p) => ({ ...p, [variantId]: r.data }));
      // Refresh the candidates list so subsequent renders know it's promoted.
      await loadAll();
    } catch (e) {
      setPromoteResult((p) => ({
        ...p,
        [variantId]: { error: formatApiError(e) },
      }));
    } finally {
      setPromoting((p) => ({ ...p, [variantId]: false }));
    }
  }

  // Build a lookup: variant_id -> candidate entry so BestVariantCard can
  // tell whether the winning variant is already promoted (and if so, the
  // existing proposal id + status).
  const learningIndex = useMemo(() => {
    const idx = {};
    for (const c of learningCandidates?.candidates || []) {
      if (c?.variant_id) idx[c.variant_id] = c;
    }
    return idx;
  }, [learningCandidates]);

  const metaFreshness = useMemo(() => {
    if (!freshness) return null;
    return (freshness.sources || []).find((s) => s.source === "meta_graph_insights") || null;
  }, [freshness]);

  const coveragePct = useMemo(() => {
    if (!freshness) return null;
    const total = freshness.total_receipts_with_external_id || 0;
    const covered = freshness.total_receipts_with_any_attribution || 0;
    if (total === 0) return null;
    return Math.round((covered / total) * 100);
  }, [freshness]);

  return (
    <Shell>
      <div className="bo-page" data-testid="insights-overview-page">
        <header className="bo-page-header">
          <div>
            <div className="bo-eyebrow">OPERATE · INSIGHTS PREVIEW</div>
            <h1 data-testid="insights-overview-title">Performance insights</h1>
            <p className="bo-page-lede">
              Which frozen creative earned which impressions, clicks, and revenue — reading live from the Meta Graph, one attribution row per (receipt · source · window).
            </p>
          </div>
          <div className="bo-page-actions" style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Link
              to="/brand-os/review-walkthrough"
              className="bo-link-sm"
              data-testid="insights-reviewer-walkthrough-link"
              style={{ fontSize: 12, color: "var(--bo-muted)" }}
              title="Open the Meta App Review walkthrough page"
            >
              Reviewer walkthrough →
            </Link>
            <button
              className="bo-btn bo-btn-primary"
              data-testid="insights-refresh-now"
              onClick={handleTickNow}
              disabled={ticking}
            >
              {ticking ? "Refreshing…" : "Refresh attributions"}
            </button>
          </div>
        </header>

        {err && <ErrorBanner error={err} testId="insights-error" />}

        {/* Phase 1 · Sign-ups by post / campaign / channel */}
        <SignupsBySourcePanel days={30} />

        {tickResult && (
          <div className="bo-card" data-testid="insights-tick-result" style={{ marginBottom: 16 }}>
            <div className="bo-eyebrow">Last refresh</div>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              {metricRow("Orgs scanned", tickResult.orgs_scanned, "tick-orgs")}
              {metricRow("Receipts scanned", tickResult.total_receipts_scanned, "tick-scanned")}
              {metricRow("Receipts recovered", tickResult.total_receipts_recovered, "tick-recovered")}
              {metricRow("Rows ingested", tickResult.total_receipts_ingested, "tick-ingested")}
              {metricRow("Backoff skipped", tickResult.total_receipts_skipped_backoff, "tick-skipped")}
            </div>
          </div>
        )}

        <div className="bo-grid-3" data-testid="insights-cards">
          {/* Card 1: Freshness pill */}
          <section className="bo-card" data-testid="insights-freshness-card">
            <header className="bo-card-header">
              <h3>Attribution freshness</h3>
              {metaFreshness && (
                <Pill tone={freshnessTone(metaFreshness.last_pulled_at)} testId="insights-freshness-pill">
                  {metaFreshness.last_pulled_at ? timeAgo(metaFreshness.last_pulled_at) : "Never"}
                </Pill>
              )}
            </header>
            {!freshness ? (
              <LoadingCard />
            ) : (freshness.sources || []).length === 0 ? (
              <EmptyState
                title="No sources reporting"
                description="Once the attribution worker ticks, Meta Graph Insights will land here."
                testId="insights-freshness-empty"
              />
            ) : (
              <ul className="bo-list-tight" data-testid="insights-freshness-sources">
                {(freshness.sources || []).map((s) => (
                  <li key={s.source} data-testid={`insights-source-${s.source}`}>
                    <span className="bo-conn-name">{s.source}</span>
                    <span style={{ color: "var(--bo-muted)", fontSize: 13 }}>
                      {s.rows_total} rows · {s.receipts_covered} receipts
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Card 2: Coverage */}
          <section className="bo-card" data-testid="insights-coverage-card">
            <header className="bo-card-header">
              <h3>Coverage</h3>
            </header>
            {!freshness ? (
              <LoadingCard />
            ) : (
              <div className="bo-metric-row">
                <div className="bo-metric">
                  <div className="bo-metric-label">Attributed receipts</div>
                  <div className="bo-metric-value" data-testid="insights-covered-count">
                    {freshness.total_receipts_with_any_attribution}
                    <span className="bo-metric-of">/{freshness.total_receipts_with_external_id || 0}</span>
                  </div>
                </div>
                {coveragePct !== null && (
                  <div className="bo-metric">
                    <div className="bo-metric-label">Percent</div>
                    <div className="bo-metric-value" data-testid="insights-covered-pct">{coveragePct}%</div>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Card 3: Worker health */}
          <section className="bo-card" data-testid="insights-worker-card">
            <header className="bo-card-header">
              <h3>Worker health</h3>
              {worker?.running !== undefined && (
                <Pill tone={worker.running ? "success" : "danger"} testId="insights-worker-pill">
                  {worker.running ? "Running" : "Stopped"}
                </Pill>
              )}
            </header>
            {!worker ? (
              <LoadingCard />
            ) : (
              <>
                <ul className="bo-list-tight">
                  <li>
                    <span className="bo-conn-name">Last tick</span>
                    <span style={{ color: "var(--bo-muted)", fontSize: 13 }}>
                      {worker.last_tick_at ? formatWhen(worker.last_tick_at) : "Never"}
                    </span>
                  </li>
                  <li>
                    <span className="bo-conn-name">Interval</span>
                    <span style={{ color: "var(--bo-muted)", fontSize: 13 }}>
                      {Math.round((worker.interval_seconds || 0) / 3600)}h
                    </span>
                  </li>
                  <li>
                    <span className="bo-conn-name">Lookback</span>
                    <span style={{ color: "var(--bo-muted)", fontSize: 13 }}>
                      {worker.lookback_days ?? "—"}d
                    </span>
                  </li>
                </ul>
                {worker.last_summary && (
                  <div style={{ marginTop: 10, fontSize: 13, color: "var(--bo-muted)" }}>
                    Last run: {worker.last_summary.total_receipts_ingested} rows ingested across{" "}
                    {worker.last_summary.orgs_scanned} orgs
                  </div>
                )}
              </>
            )}
          </section>
        </div>

        {/* Card 4: Latest attribution rows */}
        <section className="bo-card" data-testid="insights-latest-card" style={{ marginTop: 16 }}>
          <header className="bo-card-header">
            <h3>Latest attribution snapshots</h3>
            <Link to="/brand-os/operate" className="bo-link-sm">Back to Operate →</Link>
          </header>
          {!attributions ? (
            <LoadingCard />
          ) : attributions.length === 0 ? (
            <EmptyState
              title="No attribution rows yet"
              description={
                "Click Refresh attributions above to run one pull. Then the worker keeps this fresh every 6h."
              }
              testId="insights-latest-empty"
            />
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="bo-table" data-testid="insights-latest-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Source</th>
                    <th>Window</th>
                    <th>Impressions</th>
                    <th>Reach</th>
                    <th>Engagements</th>
                    <th>Clicks</th>
                    <th>Reactions</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {attributions.map((row) => {
                    const m = row.metrics || {};
                    return (
                      <tr key={row.id} data-testid={`insights-row-${row.id}`}>
                        <td>{timeAgo(row.pulled_at)}</td>
                        <td>{row.source}</td>
                        <td>{row.window_kind}</td>
                        <td>{m.impressions ?? "—"}</td>
                        <td>{m.reach ?? "—"}</td>
                        <td>{m.engagements ?? "—"}</td>
                        <td>{m.clicks ?? "—"}</td>
                        <td>{m.reactions ?? "—"}</td>
                        <td>
                          <Pill tone={row.execution_status === "ok" ? "success" : "danger"}>
                            {row.execution_status}
                          </Pill>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <div className="bo-eyebrow" style={{ marginTop: 24, opacity: 0.6 }}>
          Slice 6.3 · full rollups below · variants + per-campaign math live from Meta Graph
        </div>

        {/* -- Per-campaign rollup ------------------------------------ */}
        <section className="bo-card" data-testid="insights-campaigns-card" style={{ marginTop: 16 }}>
          <header className="bo-card-header">
            <h3>Per-campaign performance</h3>
            {campaignRollup?.totals && (
              <span style={{ fontSize: 13, color: "var(--bo-muted)" }}>
                {campaignRollup.totals.campaigns} campaign{campaignRollup.totals.campaigns === 1 ? "" : "s"} ·
                {" "}${(campaignRollup.totals.spend_usd || 0).toFixed(2)} spent
              </span>
            )}
          </header>
          {!campaignRollup ? (
            <LoadingCard />
          ) : (campaignRollup.campaigns || []).length === 0 ? (
            <EmptyState
              title="No campaign rollups yet"
              description="Once the worker enriches a receipt tied to a campaign, its rollup lands here."
              testId="insights-campaigns-empty"
            />
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="bo-table" data-testid="insights-campaigns-table">
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Receipts</th>
                    <th>Spend</th>
                    <th>Impressions</th>
                    <th>Reach</th>
                    <th>Engagements</th>
                    <th>Clicks</th>
                    <th>$/click</th>
                    <th>$/engagement</th>
                  </tr>
                </thead>
                <tbody>
                  {(campaignRollup.campaigns || []).map((c, i) => {
                    const m = c.metrics || {};
                    const cost = c.cost || {};
                    return (
                      <tr key={c.campaign_id || `unassigned-${i}`}
                          data-testid={`insights-campaign-row-${c.campaign_id || "unassigned"}`}>
                        <td>{c.campaign_name || c.campaign_id || "Unassigned"}</td>
                        <td>{c.receipts}</td>
                        <td>${(c.spend_usd || 0).toFixed(2)}</td>
                        <td>{m.impressions ?? "—"}</td>
                        <td>{m.reach ?? "—"}</td>
                        <td>{m.engagements ?? "—"}</td>
                        <td>{m.clicks ?? "—"}</td>
                        <td>{cost.cost_per_click != null ? `$${cost.cost_per_click.toFixed(4)}` : "—"}</td>
                        <td>{cost.cost_per_engagement != null ? `$${cost.cost_per_engagement.toFixed(4)}` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {campaignRollup?.notes?.impressions_availability && (campaignRollup.totals?.metrics?.impressions ?? null) === null && (
                <div className="bo-note" data-testid="insights-impressions-note"
                     style={{ marginTop: 12, padding: 12, fontSize: 13,
                              background: "var(--bo-surface-2)", borderRadius: 8, color: "var(--bo-muted)" }}>
                  <strong>Impressions locked.</strong> {campaignRollup.notes.impressions_availability}
                </div>
              )}
              {campaignRollup?.notes?.proxy_conversion && (
                <div style={{ marginTop: 8, fontSize: 12, color: "var(--bo-muted)" }}>
                  Proxy conversion metric: {campaignRollup.notes.proxy_conversion}
                </div>
              )}
            </div>
          )}
        </section>

        {/* -- Best variant callouts ---------------------------------- */}
        <div className="bo-grid-2" data-testid="insights-best-variants" style={{ marginTop: 16 }}>
          <BestVariantCard
            title="Best text variant"
            variant={variantRollup?.best_text_variant}
            idField="text_variant_id"
            testId="insights-best-text-variant"
            variantAxis="text"
            learningIndex={learningIndex}
            promoting={promoting}
            promoteResult={promoteResult}
            onPromote={handlePromoteLearning}
          />
          <BestVariantCard
            title="Best image variant"
            variant={variantRollup?.best_image_variant}
            idField="image_variant_id"
            testId="insights-best-image-variant"
            variantAxis="image"
            learningIndex={learningIndex}
            promoting={promoting}
            promoteResult={promoteResult}
            onPromote={handlePromoteLearning}
          />
        </div>
      </div>
    </Shell>
  );
}


function BestVariantCard({
  title,
  variant,
  idField,
  testId,
  variantAxis,
  learningIndex,
  promoting,
  promoteResult,
  onPromote,
}) {
  const variantId = variant?.[idField];
  const candidate = variantId ? learningIndex?.[variantId] : null;
  const alreadyPromoted = !!candidate?.already_promoted;
  const localResult = variantId ? promoteResult?.[variantId] : null;
  const busy = variantId ? !!promoting?.[variantId] : false;

  return (
    <section className="bo-card" data-testid={testId}>
      <header className="bo-card-header">
        <h3>{title}</h3>
        {variant?.[idField] && (
          <Pill tone="success">
            {(variant.metrics?.engagements ?? 0)} engagements
          </Pill>
        )}
      </header>
      {!variant || !variant[idField] ? (
        <EmptyState
          title="No variant winner yet"
          description="Once at least two receipts carry variant IDs, this card ranks them by engagement."
          testId={`${testId}-empty`}
        />
      ) : (
        <div>
          <div style={{ marginBottom: 12, fontSize: 12, color: "var(--bo-muted)", wordBreak: "break-all" }}>
            {idField}: <code>{variant[idField].slice(0, 24)}…</code>
          </div>
          <div className="bo-metric-row">
            <div className="bo-metric">
              <div className="bo-metric-label">Receipts</div>
              <div className="bo-metric-value">{variant.receipts}</div>
            </div>
            <div className="bo-metric">
              <div className="bo-metric-label">Engagements</div>
              <div className="bo-metric-value">{variant.metrics?.engagements ?? "—"}</div>
            </div>
            <div className="bo-metric">
              <div className="bo-metric-label">Clicks</div>
              <div className="bo-metric-value">{variant.metrics?.clicks ?? "—"}</div>
            </div>
            <div className="bo-metric">
              <div className="bo-metric-label">Spend</div>
              <div className="bo-metric-value">${(variant.spend_usd || 0).toFixed(2)}</div>
            </div>
          </div>
          {/* EBL-1: Promote learning */}
          <div
            style={{
              marginTop: 14,
              paddingTop: 12,
              borderTop: "1px solid var(--bo-border, #e5e5e5)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
            data-testid={`${testId}-promote-row`}
          >
            <div style={{ fontSize: 12, color: "var(--bo-muted)", maxWidth: 380 }}>
              {alreadyPromoted || localResult?.proposal_id
                ? "Sent to the review queue as a pending intelligence proposal. Performance is evidence, not truth — the owner accepts to promote."
                : "Turn this winner into a governed learning. Files a pending proposal for owner review — no Brain is mutated silently."}
            </div>
            {alreadyPromoted && !localResult?.proposal_id ? (
              <span
                className="bo-pill bo-pill-info"
                data-testid={`${testId}-promoted-chip`}
                style={{ fontSize: 12 }}
                title={candidate?.existing_proposal_id ? `proposal: ${candidate.existing_proposal_id}` : ""}
              >
                Promoted · {candidate?.existing_status || "pending"}
              </span>
            ) : localResult?.proposal_id ? (
              <span
                className="bo-pill bo-pill-success"
                data-testid={`${testId}-just-promoted-chip`}
                style={{ fontSize: 12 }}
                title={`proposal: ${localResult.proposal_id}`}
              >
                {localResult.promoted ? "Sent to review" : `Not promoted · ${localResult.reason || "see logs"}`}
              </span>
            ) : localResult?.error ? (
              <span
                className="bo-pill bo-pill-danger"
                data-testid={`${testId}-promote-error`}
                style={{ fontSize: 12 }}
              >
                Error · try again
              </span>
            ) : (
              <button
                type="button"
                className="bo-btn bo-btn-secondary"
                data-testid={`${testId}-promote-btn`}
                disabled={busy}
                onClick={() =>
                  onPromote?.({ variantAxis, variantId: variant[idField] })
                }
              >
                {busy ? "Filing…" : "Promote learning →"}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
